<template>
  <div class="page">
    <!-- ============ LEFT RAIL ============ -->
    <lp-rail
      :event="event"
      :history="orderHistory"
      :total="totalPrice"
      :currency-symbol="currencySymbol"
      :sessions-summary="sessionsSummary"
      :show-agenda="showAgendaLink"
      :agenda-label="agendaLinkLabel"
      :show-ce="!!ceInfo.show"
      :has-coupons="canApplyCoupon"
      :coupon-error="couponError"
      :donation-enabled="!!settings.donation_enabled && currentStep !== 'done'"
      :donation-title="settings.donation_title || 'Add a donation'"
      :donation="donation"
      :hide-back-link="hideBackLink"
      @open-agenda="openAgenda"
      @open-ce="openCe"
      @apply-coupon="onApplyCoupon"
      @update-donation="onDonation"
    />

    <main class="content">
      <div class="topbar">
        <lp-stepper v-if="currentStep !== 'done'" :steps="stepDef" :active-id="currentStep" />
        <span v-if="currentStep !== 'done'" class="step-num-label">Step {{ stepNum }} of {{ stepDef.length }}</span>
      </div>

      <div v-if="errorMessage" class="reg-error-banner">{{ errorMessage }}</div>

      <!-- ============ STEP 1: TICKETS ============ -->
      <section v-show="currentStep === 'tickets'" class="step-container">
        <div
          ref="stepHeader"
          class="step-header step-header--tickets"
          :class="{ 'step-header--bare': !showAttendToggle && !showCategorySelect }"
        >
          <h2 class="step-h">Choose your tickets</h2>
          <div class="sub">Pick your attendance type, ticket, and any add-ons.
            <a v-if="showAgendaLink" href="#" @click.prevent="openAgenda">{{ agendaLinkLabel }} &rarr;</a>
          </div>
          <lp-attend-toggle-inline v-if="showAttendToggle" :value="attend" :options="attendOptions" @input="setAttend" />

          <template v-if="showCategorySelect">
            <div class="category-select-heading">Please select your registration type</div>
            <lp-category-select
              :value="selectedCategoryId"
              :categories="categoryOptions"
              :currency-symbol="currencySymbol"
              @input="setCategory"
            />
          </template>
        </div>

        <!-- Phones only: compact event banner under the sticky switchers. -->
        <div class="event-banner">
          <div class="event-banner__icon">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
          </div>
          <div class="event-banner__body">
            <div class="event-banner__name">{{ event.name }}</div>
            <div v-if="event.dates" class="event-banner__meta">{{ event.dates }}</div>
            <div v-if="event.location" class="event-banner__meta event-banner__loc">
              <!-- Globe for virtual events, map-pin for a physical venue (as in the rail). -->
              <svg v-if="event.event_format === 'virtual'" aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <svg v-else aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{{ event.location }}</span>
            </div>
          </div>
        </div>
        <!-- Phones only: the step heading moved out of the sticky header, with
             the agenda / CE drawers as plain links (the rail card goes later). -->
        <div class="tickets-intro">
          <h2 class="tickets-intro__h">Choose your tickets</h2>
          <div class="tickets-intro__sub">Pick your attendance type, ticket, and any add-ons.</div>
          <div v-if="showAgendaLink || ceInfo.show" class="tickets-intro__links">
            <a v-if="showAgendaLink" href="#" @click.prevent="openAgenda">{{ agendaLinkLabel }}</a>
            <a v-if="ceInfo.show" href="#" @click.prevent="openCe">CE Info</a>
          </div>
        </div>

        <div class="tickets-area">
          <div class="ticket-col">
            <!-- Offline payments are on: say so before the form, so purchase-order
                 and check buyers don't bounce looking for a credit-card-free path. -->
            <div v-if="showOfflineNotice" class="pay-options">
              <span class="pay-options__ic">
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
              </span>
              <div class="pay-options__body">
                <p class="pay-options__t">Flexible payment options</p>
                <p class="pay-options__d">You'll have the option to pay by credit card, purchase order or another method after completing registration form.</p>
              </div>
            </div>

            <div class="ticket-list">
              <div v-if="!filteredTickets.length" class="tickets-empty">No tickets available.</div>
              <lp-ticket-card
                v-for="t in filteredTickets"
                :key="t.id"
                :ticket="t"
                :qty="ticketQty[t.id] || 0"
                :addons-lib="addonsLib"
                :addon-qty="addonQty"
                :open="openAddons.includes(t.id)"
                :currency-symbol="currencySymbol"
                @set-ticket="setTicket"
                @set-addon="setAddon"
                @toggle-addons="toggleAddons"
                @detail="showDetail"
              />
            </div>
          </div>
          <lp-details-panel :detail="detail" />
        </div>

        <!-- Phones only: discount code at the end of the ticket list (the rail,
             which holds it on desktop, is hidden at this width). -->
        <div v-if="settings.has_coupons" class="m-coupon">
          <label for="m-coupon-input" class="m-coupon__label">Discount code</label>
          <div class="m-coupon__row">
            <input
              id="m-coupon-input"
              v-model="mobileCoupon"
              type="text"
              placeholder="Enter code"
              autocomplete="off"
              @keyup.enter="applyMobileCoupon"
            />
            <button type="button" class="m-coupon__apply" @click="applyMobileCoupon">Apply</button>
          </div>
          <div v-if="couponError" class="m-coupon__error">{{ couponError }}</div>
        </div>

        <div class="step-footer">
          <!-- Phones only: the rail (and its total) sits in the page flow there,
               so the running total would otherwise scroll out of view. -->
          <div class="footer-total">
            <span class="footer-total__label">Total</span>
            <span class="footer-total__amount">{{ footerTotal }}</span>
          </div>
          <div class="footer-actions">
            <button class="btn-primary" :disabled="totalTickets === 0 || showIndicator" @click="continueFromTickets">
              Continue
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      </section>

      <!-- ============ STEP 2: ATTENDEE ============ -->
      <section v-show="currentStep === 'attendee'" class="step-container">
        <div class="step-header step-header--attendee">
          <h2 class="step-h">{{ attendeeHeading }}</h2>
          <div class="sub">{{ attendeeSubhead }}</div>
          <div v-if="attendeeSlots.length > 1 && unassignedText" class="unassigned-banner">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span class="unassigned-text">{{ unassignedText }}</span>
          </div>
        </div>

        <ValidationObserver ref="attendeeForm" tag="div">
          <!-- Multi-ticket: let the purchaser say whether they attend or just buy. -->
          <div v-if="attendeeSlots.length > 1" class="purchaser-choice">
            <p class="choice-label">Are you attending this event?</p>
            <div class="choice-grid">
              <div
                class="choice"
                :class="{ selected: purchaserIsAttendee }"
                role="radio"
                :aria-checked="purchaserIsAttendee"
                tabindex="0"
                @click="setPurchaserIsAttendee(true)"
                @keydown.enter.prevent="setPurchaserIsAttendee(true)"
                @keydown.space.prevent="setPurchaserIsAttendee(true)"
              >
                <span class="radio-dot"></span>
                <div>
                  <div class="choice-t">Yes, I'm attending</div>
                  <div class="choice-d">One ticket is mine — I'll fill in my full profile below.</div>
                </div>
              </div>
              <div
                class="choice"
                :class="{ selected: !purchaserIsAttendee }"
                role="radio"
                :aria-checked="!purchaserIsAttendee"
                tabindex="0"
                @click="setPurchaserIsAttendee(false)"
                @keydown.enter.prevent="setPurchaserIsAttendee(false)"
                @keydown.space.prevent="setPurchaserIsAttendee(false)"
              >
                <span class="radio-dot"></span>
                <div>
                  <div class="choice-t">No, I'm registering for others</div>
                  <div class="choice-d">I won't attend — just need my contact details for confirmation.</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Billing-only contact when the purchaser is not one of the attendees. -->
          <div v-if="attendeeSlots.length > 1 && !purchaserIsAttendee" class="billing-card">
            <div class="billing-head">
              <span class="billing-ic" v-html="billingIcon"></span>
              <span class="billing-t">Your details</span>
            </div>
            <div class="billing-sub">We'll send your receipt and order confirmation here. You won't take a ticket.</div>
            <lp-form-field
              v-for="f in purchaserFields"
              :key="f.name"
              :field="f"
              :value="purchaserContact[f.name]"
              :values="purchaserContact"
              scope="purchaser"
              @input="onPurchaserInput(f, $event)"
            />
          </div>

          <div v-if="attendeeSlots.length" id="attendee-cards">
            <lp-attendee-card
              v-for="(slot, idx) in attendeeSlots"
              :key="slot.id"
              :index="idx"
              :ticket-name="slot.ticket_name"
              :is-purchaser="purchaserIsAttendee && idx === 0"
              :sessions-selectable="hasSessionsStep"
              :total-slots="attendeeSlots.length"
              :schema="attendees.fields"
              :values="attendeeValues[idx] || {}"
              :fields-with-coupons="attendees.fieldsWithCoupons"
              :show-assignment="showAssignment"
              :carted-addons="cartedAddons"
              :addon-assignments="addonAssignments"
              :addon-qty="addonTotals"
              :assigned-counts="assignedCounts"
              @field-input="onFieldInput"
              @field-coupon="onFieldCoupon"
              @email-blur="onEmailBlur"
              @toggle-assign="toggleAssign"
            />
          </div>
        </ValidationObserver>

        <div class="step-footer">
          <button class="btn-outline" @click="showStep('tickets')">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
            Back
          </button>
          <div class="footer-actions">
            <button class="btn-primary" :disabled="showIndicator" @click="continueFromAttendee">
              {{ hasSessionsStep ? 'Continue to sessions' : checkoutCtaLabel }}
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      </section>

      <!-- ============ STEP 3: SESSIONS ============ -->
      <section v-show="currentStep === 'agenda'" class="step-container">
        <div class="step-header step-header--sessions">
          <h2 class="step-h">Build your agenda</h2>
          <div class="sub">Pick one session per time block. Blocks that overlap a selection are disabled.</div>
          <div class="picker-controls">
            <div class="day-jump">
              <button type="button" :class="{ active: sessionDay === 'all' }" @click="sessionDay = 'all'">All days</button>
              <button
                v-for="(d, i) in sessions.days"
                :key="i"
                type="button"
                :class="{ active: String(sessionDay) === String(i) }"
                @click="sessionDay = String(i)"
              >{{ dayShort(d.date) }}</button>
            </div>
            <div class="search-input">
              <span class="icon">
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </span>
              <input v-model="sessionSearch" autocomplete="off" aria-label="Search sessions, speakers, or topics" type="text" placeholder="Search sessions, speakers, or topics" />
            </div>
            <select v-if="sessionTopicOptions.length" v-model="sessionTopic" aria-label="Filter by topic" class="topic-filter">
              <option value="">All topics</option>
              <option v-for="t in sessionTopicOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
          </div>
        </div>

        <div id="picker-content">
          <div v-if="!filteredSessionDays.length" style="padding:40px;text-align:center;color:var(--text-tertiary);font-size: var(--fs-body);">No sessions match your filters.</div>
          <div v-for="(day, di) in filteredSessionDays" :key="di" class="day-section">
            <div class="day-header">
              <h2>{{ day.date }}</h2>
            </div>
            <lp-session-card
              v-for="group in day.sessions"
              :key="group.group_id"
              :group="group"
              :pick="sessionPicks[group.group_id] || ''"
              :disabled="isGroupBlocked(group)"
              @pick="onPick"
              @open-session="openSessionDrawer"
            />
          </div>
        </div>

        <!-- Phones only: the progress line moves out of the crowded footer. -->
        <div class="sessions-progress">
          <span><strong>{{ pickerProgressText }}</strong> blocks selected</span>
        </div>

        <div class="step-footer">
          <button class="btn-outline" @click="showStep('attendee')">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
            Back
          </button>
          <div class="footer-progress">
            <span><strong>{{ pickerProgressText }}</strong> blocks selected</span>
          </div>
          <div class="footer-actions">
            <!-- Phones get the short label; the full one doesn't fit beside Back. -->
            <button class="btn-primary" :disabled="showIndicator" @click="continueFromSessions">
              <span class="cta-label-full">{{ checkoutCtaLabel }}</span>
              <span class="cta-label-short">Continue</span>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      </section>

      <!-- ============ STEP 4: PAYMENT ============ -->
      <section v-show="currentStep === 'pay'" class="step-container">
        <div class="step-header">
          <h2 class="step-h">Payment</h2>
          <!-- The checkout note is multi-registrant guidance ("unique email per
               person"), so only show it when more than one ticket is bought. -->
          <div class="sub" v-if="settings.checkout_note && totalTickets > 1">{{ settings.checkout_note }}</div>
        </div>

        <lp-payment
          v-if="currentStep === 'pay' && paymentInfo"
          ref="payment"
          @done="handlePaymentSuccess"
          @method-change="paymentMethod = $event"
          @loading-change="paymentLoading = $event"
        />

        <div class="step-footer">
          <button class="btn-outline" :disabled="paymentBusy" @click="backFromPayment">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
            Back
          </button>
          <div class="footer-actions">
            <button class="btn-primary" :disabled="paymentBusy" @click="submitPayment">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
              {{ payButtonLabel }}
            </button>
          </div>
        </div>
      </section>

      <!-- ============ DONE ============ -->
      <section v-show="currentStep === 'done'" class="step-container">
        <div class="confirm">
          <div class="confirm-icon">
            <svg aria-hidden="true" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>
          </div>
          <h2>You're registered</h2>
          <div class="confirm-sub" v-html="doneMessage"></div>
        </div>
      </section>
    </main>

    <!-- ============ FULL AGENDA MODAL ============ -->
    <!-- Mirrors the landing page's own agenda modal (see
         resources/views/site/event/multiple/landing_default/components/modals.blade.php)
         so browsing the agenda looks identical whether it's opened from the
         landing page or from here, mid-registration. -->
    <lp-modal v-if="showAgenda" @close="closeAgenda">
      <template #title>
        <h2 style="margin:0; font-size: var(--fs-h3);">{{ agendaSessions.length }} session{{ agendaSessions.length !== 1 ? 's' : '' }} · {{ orderedDayIds.length }} day{{ orderedDayIds.length !== 1 ? 's' : '' }}<template v-if="agendaTrackCount"> · {{ agendaTrackCount }} track{{ agendaTrackCount !== 1 ? 's' : '' }}</template></h2>
      </template>
      <template #controls>
        <div class="agenda-search">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input autocomplete="off" aria-label="Search sessions, presenters, or topics…" type="text" v-model="agendaSearch" placeholder="Search sessions, presenters, or topics…" />
        </div>
        <div class="agenda-filters">
          <select aria-label="Filter by day" v-model="agendaDayFilter" class="agenda-select">
            <option value="all">All days</option>
            <option v-for="id in orderedDayIds" :key="id" :value="String(id)">{{ agendaDayNames[id] }}</option>
          </select>
          <select v-if="agendaTrackCount" aria-label="Filter by track" v-model="agendaTrackFilter" class="agenda-select">
            <option value="all">All tracks</option>
            <option v-for="(name, id) in agendaTrackNames" :key="id" :value="String(id)">{{ name }}</option>
          </select>
        </div>
      </template>

      <div v-if="!agendaGroups.length" class="agenda-empty">No sessions match your filters. Try clearing search or selecting a different day or track.</div>
      <template v-else>
        <div class="agenda-day-section" v-for="group in agendaGroups" :key="group.day">
          <h3 class="agenda-day-section-title">{{ group.label }} · {{ group.sessions.length }} session{{ group.sessions.length !== 1 ? 's' : '' }}</h3>
          <div class="session-cards">
            <article class="session-card" v-for="s in group.sessions" :key="s._id" @click="openSessionDrawer(s)" style="cursor:pointer">
              <div class="session-card-meta">
                <span class="session-time">{{ s.time }}</span>
                <span v-if="s.trackLabel" class="session-track-pill" :data-track="s.trackColor">{{ s.trackLabel }}</span>
              </div>
              <h3>{{ s.title }}</h3>
              <p class="session-presenter">{{ s.presenter }}</p>
              <div v-if="s.tags && s.tags.length" class="session-tags">
                <span class="session-tag" v-for="(tag, ti) in s.tags" :key="ti">{{ tag }}</span>
              </div>
            </article>
          </div>
        </div>
      </template>
    </lp-modal>

    <!-- ============ CE INFO MODAL ============ -->
    <lp-modal v-if="showCe && ceInfo.show" @close="closeCe">
      <template #title>
        <h3 style="font-size: var(--fs-h5);font-weight:600;letter-spacing:-0.01em;margin:0 0 2px;">CE Info</h3>
        <div class="sub" style="font-size: var(--fs-small);color:var(--text-secondary);">Continuing education credits &amp; accreditation for this event.</div>
      </template>

      <div v-if="ceInfo.show_attend_live_message" class="ce-section">
        <div class="ce-callout">
          <span class="ce-callout-ic" v-html="ceIcon('info')"></span>
          <p><strong>Important:</strong> You must attend live (in real time) to earn CE credits. Recordings do not qualify.</p>
        </div>
      </div>

      <div v-if="ceTotalHours != null || ceColumns.length || ceTopics.length" class="ce-section">
        <!-- Single conference-wide total from Event Details "Total CE Hours"; hidden when unset.
             Event topics (conference-wide chips with CE hours) live inside the same card. -->
        <div v-if="ceTotalHours != null || ceTopics.length" class="ce-stat-card">
          <div v-if="ceTotalHours != null" class="ce-stat">
            <span class="ce-stat-num">{{ formatCeHours(ceTotalHours) }}</span>
            <span class="ce-stat-unit">CE credit hours</span>
          </div>
          <div v-if="ceTopics.length" class="ce-topics-row" :class="{ 'ce-topics-row--with-stat': ceTotalHours != null }">
            <span class="topic-chip topic-chip--h" v-for="(t, i) in ceTopics" :key="i">{{ t.title }}<span class="topic-chip-h">{{ t.hours }}h</span></span>
          </div>
        </div>
        <div v-if="ceColumns.length" class="ce-formats-grid" :class="{ 'ce-formats-grid--single': ceColumns.length === 1 }">
          <div class="ce-format-column" v-for="col in ceColumns" :key="col.key">
            <div class="ce-format-header">
              <span class="ce-format-badge" :class="'ce-format-badge-' + col.badge">
                <span class="ce-format-badge-ic" v-html="ceIcon(col.badge)"></span>{{ col.label }}
              </span>
            </div>
            <div class="ce-approvals">
              <div class="ce-approvals-head">
                <span>Approved for</span>
                <span class="ce-approvals-count">{{ col.bodies.length }}</span>
              </div>
              <div class="ce-body-card" :class="{ 'ce-body-card--logo': body.logo_url }" v-for="body in col.bodies" :key="body.id">
                <div v-if="body.logo_url" class="ce-body-logo">
                  <img :src="body.logo_url" :alt="body.name" loading="lazy" />
                </div>
                <div class="ce-body-main">
                  <div class="ce-body-name">
                    <span v-if="!body.logo_url" class="ce-body-ic" v-html="ceIcon('shield')"></span>{{ body.name }}
                  </div>
                  <div class="ce-body-detail" v-html="body.statement_html"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="ceInfo.target_audience" class="ce-section ce-section--doc">
        <h4><span class="ce-h4-ic" v-html="ceIcon('audience')"></span>Target audience</h4>
        <p>{{ ceInfo.target_audience }}</p>
      </div>
      <div v-if="ceInfo.learning_objectives && ceInfo.learning_objectives.length" class="ce-section ce-section--doc">
        <h4><span class="ce-h4-ic" v-html="ceIcon('objectives')"></span>Event learning objectives</h4>
        <ol class="ce-list"><li v-for="(o, i) in ceInfo.learning_objectives" :key="i">{{ o }}</li></ol>
      </div>
      <div v-if="ceInfo.delivery_html" class="ce-section ce-section--doc">
        <h4><span class="ce-h4-ic" v-html="ceIcon('certificate')"></span>Certificate delivery</h4>
        <p v-html="ceInfo.delivery_html"></p>
      </div>
      <div v-for="(p, i) in (ceInfo.policies || [])" :key="'pol' + i" class="ce-section ce-section--doc">
        <h4><span class="ce-h4-ic" v-html="ceIcon('policy')"></span>{{ p.title }}</h4>
        <p v-html="p.description"></p>
      </div>
    </lp-modal>

    <!-- ============ SESSION DETAIL DRAWER ============ -->
    <!-- Body is <lp-session-detail> (ui/lp-session-detail.vue), shared with the
         landing page's session drawer so both surfaces show the same
         information. `presenters` is passed because the sessions step's payload
         carries name-only presenters — see drawerPresenters. -->
    <lp-drawer v-if="drawerSession" :show-back="false" @close="closeDrawer" @back="closeDrawer">
      <lp-session-detail
        :session="drawerSession"
        :day-names="agendaDayNames"
        :presenters="drawerPresenters"
        @select-presenter="openFacultyDetail"
      />
    </lp-drawer>

    <!-- ============ PRESENTER DETAIL DRAWER ============ -->
    <!-- Same component (and so the same content) as the landing page's speaker
         drawer — see ui/lp-faculty-detail.vue. -->
    <lp-drawer v-if="facultyDetail" :show-back="false" @close="closeFacultyDetail" @back="closeFacultyDetail">
      <lp-faculty-detail
        :faculty="facultyDetail"
        :day-names="agendaDayNames"
        sessions-label="Presenting at this event"
        @open-session="openSessionFromFaculty"
      />
    </lp-drawer>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { http } from '../../api/http'

import lpRail from './components/lp-rail.vue'
import lpStepper from './components/lp-stepper.vue'
import lpDetailsPanel from './components/lp-details-panel.vue'
import lpTicketCard from './components/lp-ticket-card.vue'
import lpAttendeeCard from './components/lp-attendee-card.vue'
import lpFormField from './components/lp-form-field.vue'
import lpSessionCard from './components/lp-session-card.vue'
import lpPayment from './components/lp-payment.vue'
import lpCategorySelect from './components/lp-category-select.vue'
import { formatCeHours } from '../../ui/session'

const PERSON_ICON = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></svg>'
const GLOBE_ICON = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>'

// Line icons for the CE Info modal (section headers, format badges, callouts).
// format badges reuse the attend-toggle glyphs so the two surfaces stay in sync.
const _svg = (paths) => '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + paths + '</svg>'
const CE_ICONS = {
  inperson: PERSON_ICON,
  virtual: GLOBE_ICON,
  shield: _svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>'),
  audience: _svg('<path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
  objectives: _svg('<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>'),
  certificate: _svg('<circle cx="12" cy="8" r="6"/><path d="M8.5 13 7 22l5-3 5 3-1.5-9"/>'),
  policy: _svg('<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/>'),
  info: _svg('<circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/>'),
}

// Billing/contact fields collected for a purchaser who buys for others.
// Mirrors the old modal's customer step; names match the form-builder schema
// and what RegistrationCustomerService::syncFromPurchaser reads (state id →
// derives country; no separate country select on the new page).
const BILLING_FIELD_NAMES = [
  'first_name', 'last_name', 'email', 'email_confirmation',
  'company', 'address_1', 'city', 'state', 'other_state', 'zip_code', 'phone_number',
]

export default {
  name: 'registration-page',
  components: {
    lpRail, lpStepper, lpDetailsPanel, lpTicketCard, lpAttendeeCard, lpFormField, lpSessionCard, lpPayment,
    lpCategorySelect,
  },
  props: {
    pathname: { type: String, required: true },
    csrfToken: { type: String, default: '' },
    eventInfo: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // Hide the "Back to event page" link when this page is a standalone
      // entry point: opened via a direct/embed link (?embed=1) or served
      // inside the iframe widget route. Captured once here, before the SPA
      // strips query params on payment redirects. A normal in-flow visit
      // from the landing keeps the link.
      hideBackLink:
        new URLSearchParams(window.location.search).has('embed') ||
        window.location.pathname.includes('/registration-widget'),

      currentStep: 'tickets',
      attend: 'in-person',
      // Ticket category currently being browsed. 0 is the "Other" bucket;
      // null means the categoryOptions watcher hasn't picked one yet.
      selectedCategoryId: null,
      ticketQty: {},
      addonQty: {},
      openAddons: [],
      detail: null,
      donation: 0,
      couponError: '',
      mobileCoupon: '',

      attendeeValues: [],
      // Multi-ticket only: whether the purchaser takes one of the tickets.
      // When false, the purchaser is a billing-only contact and every ticket
      // goes to someone else.
      purchaserIsAttendee: true,
      purchaserContact: { first_name: '', last_name: '', email: '', email_confirmation: '' },
      addonAssignments: {},
      sessionPicks: {},
      sessionDay: 'all',
      sessionSearch: '',
      sessionTopic: '',
      paymentMethod: 'card',
      paymentLoading: false,
      // Rail changes (coupon / donation) in flight. On the payment step paying
      // waits for them, since the payment is re-prepared for the new total.
      basketChangesInFlight: 0,

      showAgenda: false,
      agendaSearch: '',
      agendaDayFilter: 'all',
      agendaTrackFilter: 'all',

      showCe: false,
      drawerSession: null,
      facultyDetail: null,

      basketDebounce: null,
      doneMessage: 'Confirmation and calendar details are on their way to your email.',
    }
  },
  computed: {
    ...mapState('registration', ['event', 'ceInfo', 'settings', 'tickets', 'ticketCategories', 'basket', 'attendees', 'sessions', 'agenda', 'faculty', 'paymentInfo', 'showIndicator', 'errorMessage']),
    ...mapGetters('registration', ['currencySymbol', 'orderHistory', 'totalPrice', 'hasSessionsStep', 'isFree']),

    attendKey() { return this.attend === 'virtual' ? 'virtual' : 'inperson' },

    // "View full agenda" links (rail button + tickets-step header) are hidden
    // when the organizer turned on Hide Sessions — matches the landing page's
    // Agenda-tab behavior. Named *Link to avoid the showAgenda data prop that
    // toggles the agenda modal.
    showAgendaLink() {
      return !this.event.hide_sessions
    },

    // Single-session events skip the agenda list and open the session drawer
    // straight away (see openAgenda), so "full agenda" would be misleading.
    agendaLinkLabel() {
      return this.event.is_single_session ? 'View session details' : 'View full agenda'
    },

    showAttendToggle() {
      return (this.tickets.inperson || []).length > 0 && (this.tickets.virtual || []).length > 0
    },

    // Offline payments let attendees settle by check or purchase order after
    // registering. Surfacing it on the tickets step (not just at checkout)
    // answers "do you take a PO?" before anyone starts filling the form.
    showOfflineNotice() {
      return !!this.settings.offline_payments
    },

    attendOptions() {
      const city = this.event.city
      return [
        { value: 'in-person', label: 'In-person', desc: city ? `Attend on-site in ${city}` : 'Attend on-site', icon: PERSON_ICON },
        { value: 'virtual', label: 'Virtual', desc: 'Attend online', icon: GLOBE_ICON },
      ]
    },

    stepDef() {
      const steps = [
        { id: 'tickets', label: 'Tickets' },
        { id: 'attendee', label: 'Attendee' },
      ]
      if (this.hasSessionsStep) steps.push({ id: 'agenda', label: 'Sessions' })
      // A $0 cart never reaches the pay step — startPayment() confirms it
      // outright — so only advertise the step while something in the cart
      // actually costs money. This tracks the live basket total, so it
      // answers picking a paid ticket on the tickets step (~400ms behind the
      // debounced basket update, same as the rail's total) as well as a
      // coupon zeroing the order or a donation lifting it back off zero.
      if (!this.isFree) steps.push({ id: 'pay', label: 'Payment' })
      return steps
    },
    // Label for the button that ends the flow: a $0 cart finishes on the spot.
    checkoutCtaLabel() {
      return this.isFree ? 'Complete registration' : 'Continue to payment'
    },
    stepNum() {
      const i = this.stepDef.findIndex(s => s.id === this.currentStep)
      return i >= 0 ? i + 1 : 1
    },
    // Discount codes belong to building the order: once the buyer is paying
    // (or has paid) the rail no longer offers one.
    canApplyCoupon() {
      return !!this.settings.has_coupons && !['pay', 'done'].includes(this.currentStep)
    },

    serverTickets() {
      return this.tickets[this.attendKey] || []
    },

    // ---------- Ticket categories ----------
    // Category ids the event actually defines. A ticket pointing at anything
    // else (a since-deleted category) is treated as uncategorized rather than
    // vanishing from the list.
    knownCategoryIds() {
      return new Set((this.ticketCategories || []).map(c => c.id))
    },
    // Bucket a ticket belongs to: its category id, or 0 for uncategorized.
    categoryOptions() {
      if (!(this.ticketCategories || []).length) return []

      // Only categories with something to show in the current attendance type
      // — otherwise switching in-person/virtual can land on an empty list.
      const options = this.ticketCategories
        .filter(c => this.serverTickets.some(t => this.ticketBucket(t) === c.id))
        .map(c => ({ id: c.id, title: c.title, description: c.description, ...this.categoryStats(c.id) }))

      if (this.serverTickets.some(t => this.ticketBucket(t) === 0)) {
        options.push({ id: 0, title: 'Other tickets', description: '', ...this.categoryStats(0) })
      }
      return options
    },
    // One option is not a choice — show the picker only when it can filter.
    showCategorySelect() {
      return this.categoryOptions.length > 1
    },

    filteredTickets() {
      const items = this.showCategorySelect
        ? this.serverTickets.filter(t => this.ticketBucket(t) === this.selectedCategoryId)
        : this.serverTickets

      return items.map(t => ({
        id: t.id,
        name: t.title,
        desc: t.description,
        includes: t.includes || [],
        price: Number(t.price),
        priceSub: t.special_price_description || '',
        max: t.available_limit,
        // Container ticket: it owns no sessions and sells them through add-ons,
        // so its own (usually $0) price is not what the attendee pays — the
        // add-on rows below carry the real amounts.
        hidePrice: !t.has_sessions && (t.addons || []).length > 0,
        // Full per-ticket addon objects (each ticket has its own availability).
        addons: (t.addons || []).map(a => ({
          id: a.id,
          name: a.title,
          desc: a.description,
          price: Number(a.price),
          max: a.available_limit,
        })),
      }))
    },
    addonsLib() {
      const lib = {}
      ;[...(this.tickets.inperson || []), ...(this.tickets.virtual || [])].forEach(t => {
        (t.addons || []).forEach(a => {
          lib[a.id] = {
            id: a.id,
            name: a.title,
            desc: a.description,
            price: Number(a.price),
            max: a.available_limit,
          }
        })
      })
      return lib
    },
    totalTickets() {
      return Object.values(this.ticketQty).reduce((s, q) => s + (q || 0), 0)
    },
    cartedAddons() {
      return Object.values(this.addonsLib).filter(a => this.addonTotalQty(a.id) > 0)
    },
    // Cart-wide totals per addon id (for the attendee add-on assignment UI).
    addonTotals() {
      const totals = {}
      Object.values(this.addonsLib).forEach(a => { totals[a.id] = this.addonTotalQty(a.id) })
      return totals
    },
    attendeeSlots() {
      return this.attendees.list || []
    },
    showAssignment() {
      return this.attendeeSlots.length > 1 && this.cartedAddons.length > 0
    },
    assignedCounts() {
      const m = {}
      Object.keys(this.addonAssignments).forEach(id => { m[id] = (this.addonAssignments[id] || []).length })
      return m
    },
    attendeeHeading() {
      const n = this.attendeeSlots.length
      return n > 1 ? `Attendees (${n} tickets)` : 'Attendee details'
    },
    attendeeSubhead() {
      const n = this.attendeeSlots.length
      if (n <= 1) return ''
      return 'Enter a unique email for each person. Each attendee gets an email to complete their own profile.'
    },
    billingIcon() { return PERSON_ICON },
    // Billing/contact fields for the buying-for-others block. Reuses the real
    // form-builder fields (labels, state options, required flags, other_state
    // companion) so it stays consistent with the attending purchaser's form.
    purchaserFields() {
      const byName = {}
      ;(this.attendees.fields || []).forEach(f => { byName[f.name] = f })
      return BILLING_FIELD_NAMES.map(n => byName[n]).filter(Boolean)
    },
    unassignedText() {
      if (this.attendeeSlots.length <= 1) return ''
      const unassigned = this.cartedAddons
        .map(a => ({ a, remaining: this.addonTotalQty(a.id) - (this.assignedCounts[a.id] || 0) }))
        .filter(x => x.remaining > 0)
      if (!unassigned.length) return ''
      return 'Unassigned add-ons: ' + unassigned.map(x => `${x.remaining} × ${x.a.name}`).join(', ') + '.'
    },
    hasUnassigned() {
      return this.attendeeSlots.length > 1 && !!this.unassignedText
    },
    sessionsSummary() {
      const picked = Object.values(this.sessionPicks).filter(Boolean).length
      return picked ? `+ ${picked} session${picked > 1 ? 's' : ''} selected` : ''
    },
    pickerProgressText() {
      const decided = Object.values(this.sessionPicks).filter(Boolean).length
      const required = (this.sessions.days || []).reduce((s, d) => s + d.sessions.filter(g => !this.isGroupBlocked(g)).length, 0)
      return `${decided} of ${required}`
    },
    sessionTopicOptions() {
      const map = {}
      ;(this.sessions.days || []).forEach(d => d.sessions.forEach(g => (g.items || []).forEach(it => {
        ;(it.topics || []).forEach(t => { if (t.title) map[t.id] = t.title })
      })))
      return Object.keys(map).map(id => ({ value: String(id), label: map[id] }))
    },
    filteredSessionDays() {
      const search = this.sessionSearch.toLowerCase()
      const topic = this.sessionTopic
      const matches = (it) => {
        const presenters = (it.presenters || []).map(p => p.name).join(' ')
        const topicTitles = (it.topics || []).map(t => t.title).join(' ')
        const hay = `${it.name} ${presenters} ${topicTitles}`.toLowerCase()
        const matchTopic = !topic || (it.topics || []).some(t => String(t.id) === String(topic))
        return (!search || hay.includes(search)) && matchTopic
      }
      return (this.sessions.days || [])
        .filter((d, i) => this.sessionDay === 'all' || String(this.sessionDay) === String(i))
        .map(d => ({
          date: d.date,
          // Keep each group (group_id/concurrent_groups intact) but drop items
          // that don't match, and hide groups left empty.
          sessions: d.sessions
            .map(g => Object.assign({}, g, { items: (g.items || []).filter(matches) }))
            .filter(g => g.items.length),
        }))
        .filter(d => d.sessions.length)
    },

    // ----- Agenda modal (from GET event/agenda) -----
    // Day ids in chronological order. dayNames is an id-keyed object, and
    // Object.keys() reorders integer-like keys numerically (i.e. by day id, not
    // by date) — a day added later but dated earlier would sort last. Sessions
    // arrive pre-sorted by start_at, so first-appearance order is date order.
    orderedDayIds() {
      const seen = new Set()
      const ids = []
      ;(this.agendaSessions || []).forEach(s => {
        const d = String(s.day)
        if (!seen.has(d)) { seen.add(d); ids.push(d) }
      })
      return ids
    },
    // Same shape and filtering rules as the landing page's own agenda modal
    // (home.blade.php's filteredSessions/agendaGroups, multiple theme) — kept
    // in sync deliberately so browsing the agenda looks and behaves the same
    // whether it's opened from the landing page or from here.
    agendaTrackNames() {
      return (this.agenda && this.agenda.trackNames) || {}
    },
    agendaTrackCount() {
      return Object.keys(this.agendaTrackNames).length
    },
    filteredAgendaSessions() {
      const q = (this.agendaSearch || '').toLowerCase().trim()
      return this.agendaSessions.filter(s => {
        if (this.agendaDayFilter !== 'all' && String(s.day) !== this.agendaDayFilter) return false
        if (this.agendaTrackFilter !== 'all' && String(s.track) !== this.agendaTrackFilter) return false
        if (q) {
          const haystack = (s.title + ' ' + (s.presenter || '') + ' ' + (this.agendaTrackNames[s.track] || '') + ' ' + (s.tags || []).join(' ')).toLowerCase()
          if (!haystack.includes(q)) return false
        }
        return true
      })
    },
    agendaGroups() {
      const groups = []
      this.orderedDayIds.forEach(day => {
        const sessions = this.filteredAgendaSessions.filter(s => String(s.day) === day)
        if (sessions.length) groups.push({ day, label: this.agendaDayNames[day] || '', sessions })
      })
      return groups
    },

    ceTotalHours() {
      // Conference-wide total from Event Details "Total CE Hours"; null hides the stat.
      const h = this.ceInfo.total_hours
      return h == null ? null : Number(h)
    },
    ceColumns() {
      // One column per event format, kept only when it has accrediting bodies to
      // list — the CE hours total is now shown once, above the columns.
      const ip = this.ceInfo.in_person || {}
      const v = this.ceInfo.virtual || {}
      const ipCol = { key: 'ip', label: 'In-person', badge: 'inperson', bodies: ip.bodies || [] }
      const vCol = { key: 'v', label: 'Virtual', badge: 'virtual', bodies: v.bodies || [] }

      let cols
      if (this.ceInfo.event_format === 'hybrid') cols = [ipCol, vCol]
      else if (this.ceInfo.event_format === 'virtual') cols = [vCol]
      else cols = [ipCol]

      return cols.filter((col) => col.bodies.length)
    },

    agendaSessions() {
      return (this.agenda && this.agenda.sessions) || []
    },
    agendaDayNames() {
      return (this.agenda && this.agenda.dayNames) || {}
    },
    // Flat roster; the API groups it into keynotes and breakouts.
    facultyList() {
      const roster = this.faculty || {}
      return (roster.keynotes || []).concat(roster.breakouts || [])
    },
    drawerPresenters() {
      if (!this.drawerSession) return []
      // The sessions step sends presenters as { id, name } only
      // (AgendaGroupService), while the agenda payload carries role, photo and
      // bio. Prefer the richer record so the rows look the same wherever the
      // drawer was opened from; openSessionDrawer loads the agenda for this.
      const detailed = this.agendaSessions.find((s) => s.id === this.drawerSession.id)
      return (detailed && detailed.presenters) || this.drawerSession.presenters || []
    },
    // Conference-wide topics ({ title, icon, hours }) from the CE-info payload.
    ceTopics() {
      return this.ceInfo.topics || []
    },
    paymentBusy() {
      return this.paymentLoading || this.basketChangesInFlight > 0 || !!this.basketDebounce
    },
    payButtonLabel() {
      if (this.paymentBusy) return 'Processing…'
      if (this.paymentMethod === 'offline') return 'Complete registration'
      const amount = Number(this.totalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `Pay ${this.currencySymbol}${amount}`
    },
    footerTotal() {
      const amount = Number(this.totalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${this.currencySymbol}${amount}`
    },
  },
  watch: {
    // Keep the browsed category on something that exists: pick the first one
    // once the catalog arrives, and re-pick when switching attendance type
    // takes the current one off the list.
    categoryOptions: {
      immediate: true,
      handler(options) {
        if (!options.length) {
          this.selectedCategoryId = null
        } else if (!options.some(o => o.id === this.selectedCategoryId)) {
          this.selectedCategoryId = options[0].id
        }
      },
    },
  },
  created() {
    if (this.csrfToken) http.setCsrf(this.csrfToken)
    this.$store.dispatch('registration/init', { pathname: this.pathname })
    this.handleMount()
  },
  mounted() {
    // The sticky details panel must park below the sticky topbar + step-header
    // stack. That stack's height is dynamic (attendance toggle shown/hidden,
    // wrapping titles), so measure it and expose it as a CSS var the panel reads.
    this.syncStickyOffset()
    if (typeof ResizeObserver !== 'undefined' && this.$refs.stepHeader) {
      this._stickyObserver = new ResizeObserver(() => this.syncStickyOffset())
      this._stickyObserver.observe(this.$refs.stepHeader)
    }
  },
  beforeDestroy() {
    if (this._stickyObserver) {
      this._stickyObserver.disconnect()
      this._stickyObserver = null
    }
  },
  methods: {
    formatCeHours,
    ...mapActions('registration', [
      'bootstrap', 'updateBasket', 'applyCoupon', 'fetchAttendees', 'updateAttendees',
      'checkField', 'checkMembershipEmail', 'fetchSessions', 'updateSessions',
      'preparePayment', 'confirmPayment', 'fetchAgenda', 'fetchFaculty', 'useBasketToken',
    ]),
    ceIcon(name) { return CE_ICONS[name] || '' },

    // Recompute where the sticky details panel should park: below the topbar
    // (step-header sticks at --topbar-h) plus the live step-header height + gap.
    syncStickyOffset() {
      const header = this.$refs.stepHeader
      if (!header) return
      const topbar = parseInt(getComputedStyle(this.$el).getPropertyValue('--topbar-h'), 10) || 60
      const top = topbar + header.offsetHeight + 16
      this.$el.style.setProperty('--details-sticky-top', top + 'px')
    },

    async handleMount() {
      const params = new URLSearchParams(window.location.search)
      const payment = params.get('payment')

      if (payment === 'success') {
        await this.bootstrap()
        this.doneMessage = this.settings.success_payment_message || this.doneMessage
        this.currentStep = 'done'
        this.stripQuery()
        return
      }

      // A payment-retry redirect carries the basket it was paying for; adopt it
      // into this tab so the restored cart threads on every follow-up request.
      if (payment === 'failed' && params.get('basket_token')) {
        await this.useBasketToken(params.get('basket_token'))
      }
      await this.bootstrap()
      this.setDefaultAttend()
      this.syncQtyFromServer()
      this.donation = Number(this.basket.donation || 0)

      if (payment === 'failed') {
        this.$store.commit('registration/setError', 'Your payment was not completed. Please try again.')
      }
      this.stripQuery()
    },
    stripQuery() {
      if (!(window.history && window.history.replaceState)) return
      // Remove only the transient payment-redirect params so a refresh can't
      // re-trigger them. Everything else stays in the URL — notably `embed`
      // (keeps the back link hidden on reload) and the widget's `path`.
      const params = new URLSearchParams(window.location.search)
      ;['payment', 'basket_token'].forEach((key) => params.delete(key))
      const query = params.toString()
      const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      window.history.replaceState({}, document.title, url)
    },

    setDefaultAttend() {
      // Default to whichever attendance type actually has tickets, so the
      // list isn't empty when only one type is offered.
      const hasInperson = (this.tickets.inperson || []).length > 0
      const hasVirtual = (this.tickets.virtual || []).length > 0
      if (!hasInperson && hasVirtual) this.attend = 'virtual'
      else this.attend = 'in-person'
    },
    syncQtyFromServer() {
      const tq = {}
      const aq = {}
      ;[...(this.tickets.inperson || []), ...(this.tickets.virtual || [])].forEach(t => {
        tq[t.id] = t.selected || 0
        // Addon quantities are per-ticket (the same addon offered by two tickets
        // is tracked separately), keyed by `${ticketId}:${addonId}`.
        ;(t.addons || []).forEach(a => { aq[this.addonKey(t.id, a.id)] = a.selected || 0 })
      })
      this.ticketQty = tq
      this.addonQty = aq
    },
    addonKey(ticketId, addonId) {
      return `${ticketId}:${addonId}`
    },
    // Total selected quantity of an addon across all tickets (for cart-wide
    // views like the attendee add-on assignment).
    addonTotalQty(addonId) {
      return Object.keys(this.addonQty).reduce((sum, key) => {
        return key.endsWith(':' + addonId) ? sum + (this.addonQty[key] || 0) : sum
      }, 0)
    },

    // ---------- Tickets step ----------
    ticketBucket(t) {
      return this.knownCategoryIds.has(t.ticket_category_id) ? t.ticket_category_id : 0
    },
    // Selected-ticket count and price range of one category bucket, for the
    // phone segmented control. Container tickets (price carried by add-ons)
    // don't count towards the range.
    categoryStats(id) {
      let count = 0
      const prices = []
      this.serverTickets.forEach(t => {
        if (this.ticketBucket(t) !== id) return
        count += this.ticketQty[t.id] || 0
        if (t.has_sessions || !(t.addons || []).length) prices.push(Number(t.price))
      })
      return {
        count,
        minPrice: prices.length ? Math.min(...prices) : null,
        maxPrice: prices.length ? Math.max(...prices) : null,
      }
    },
    setCategory(id) {
      if (id === this.selectedCategoryId) return
      this.selectedCategoryId = id
      // The details panel may be describing a ticket that just left the list.
      this.detail = null
      // Quantities are deliberately kept: the cart spans categories, same as
      // the legacy registration modal.
    },
    setAttend(v) {
      if (v === this.attend) return
      this.attend = v
      // Clear selection to avoid mixing attendance types.
      this.ticketQty = {}
      this.addonQty = {}
      this.addonAssignments = {}
      this.detail = null
      this.queueBasketUpdate()
    },
    setTicket({ id, qty }) {
      this.$set(this.ticketQty, id, qty)
      if (qty === 0) {
        // Drop this ticket's own addon quantities.
        const t = this.serverTickets.find(x => x.id === id)
        if (t) (t.addons || []).forEach(a => this.$set(this.addonQty, this.addonKey(id, a.id), 0))
      }
      this.queueBasketUpdate()
    },
    setAddon({ ticketId, id, qty }) {
      this.$set(this.addonQty, this.addonKey(ticketId, id), qty)
      this.queueBasketUpdate()
    },
    toggleAddons(id) {
      const i = this.openAddons.indexOf(id)
      if (i >= 0) this.openAddons.splice(i, 1)
      else this.openAddons.push(id)
    },
    showDetail({ type, id }) {
      if (type === 'ticket') {
        const t = this.serverTickets.find(x => x.id === id)
        if (!t) return
        const hidePrice = !t.has_sessions && (t.addons || []).length > 0

        this.detail = {
          eyebrow: this.attendKey === 'virtual' ? 'Virtual ticket' : 'In-person ticket',
          title: t.title,
          price: hidePrice ? null : Number(t.price),
          // "What's included" bullets built server-side; the ticket description
          // renders as the badge/callout underneath.
          includes: t.includes || [],
          noteHtml: t.description || null,
          currencySymbol: this.currencySymbol,
          currency: this.settings.currency ? this.settings.currency.toUpperCase() : 'USD',
        }
      } else {
        const a = this.addonsLib[id]
        if (!a) return
        this.detail = {
          eyebrow: 'Add-on',
          title: a.name,
          price: a.price,
          description: a.desc,
          note: null,
          currencySymbol: this.currencySymbol,
          currency: this.settings.currency ? this.settings.currency.toUpperCase() : 'USD',
        }
      }
    },

    buildBasketPayload() {
      const all = [...(this.tickets.inperson || []), ...(this.tickets.virtual || [])]
      return {
        tickets: all.map(t => {
          const qty = this.ticketQty[t.id] || 0
          return {
            id: t.id,
            quantity: qty,
            addons: (t.addons || []).map(a => ({
              id: a.id,
              quantity: qty > 0 ? (this.addonQty[this.addonKey(t.id, a.id)] || 0) : 0,
            })),
          }
        }),
        donation: this.donation,
      }
    },
    queueBasketUpdate() {
      if (this.basketDebounce) clearTimeout(this.basketDebounce)
      this.basketDebounce = setTimeout(() => { this.flushBasketUpdate() }, 400)
    },
    async flushBasketUpdate() {
      if (this.basketDebounce) { clearTimeout(this.basketDebounce); this.basketDebounce = null }
      this.basketChangesInFlight++
      try {
        await this.updateBasket(this.buildBasketPayload())
        this.syncQtyFromServer()
        this.refreshDetail()
        await this.refreshPreparedPayment()
      } catch (e) {
        /* error surfaced by store banner */
      } finally {
        this.basketChangesInFlight--
      }
    },
    refreshDetail() {
      if (this.detail) {
        const t = this.serverTickets.find(x => x.title === this.detail.title)
        if (t) this.showDetail({ type: 'ticket', id: t.id })
      }
    },

    onDonation(value) {
      this.donation = value
      this.queueBasketUpdate()
    },
    applyMobileCoupon() {
      const code = this.mobileCoupon.trim()
      if (code) this.onApplyCoupon(code)
    },
    async onApplyCoupon(code) {
      this.couponError = ''
      this.basketChangesInFlight++
      try {
        await this.applyCoupon(code)
        this.syncQtyFromServer()
        // Never throws: lp-payment surfaces its own re-prepare errors.
        await this.refreshPreparedPayment()
      } catch (e) {
        this.couponError = e.message || 'Coupon could not be applied.'
      } finally {
        this.basketChangesInFlight--
      }
    },
    // The donation stays editable on the payment step (and a coupon sent just
    // before leaving the previous step can land there), but the PaymentIntent
    // was created for the total at the time it was prepared — re-prepare it, or
    // the card is charged the stale amount while the summary and the Pay
    // button show the new one.
    async refreshPreparedPayment() {
      if (this.currentStep !== 'pay') return
      if (this.isFree) {
        // Nothing to charge any more: the previous step's button now completes
        // the registration as free.
        this.backFromPayment()
        return
      }
      if (this.$refs.payment) await this.$refs.payment.refresh()
    },

    async continueFromTickets() {
      // Cancel any pending incremental update and send a final `confirm` update,
      // which additionally validates that sessionless tickets carry enough
      // session add-ons. Stay on this step if the server rejects it.
      if (this.basketDebounce) { clearTimeout(this.basketDebounce); this.basketDebounce = null }
      try {
        await this.updateBasket({ ...this.buildBasketPayload(), confirm: true })
        this.syncQtyFromServer()
        this.refreshDetail()
      } catch (e) {
        return
      }
      await this.fetchAttendees()
      this.initAttendeeValues()
      this.showStep('attendee')
    },

    // ---------- Attendee step ----------
    // Full form-builder defaults for the purchaser slot (address, license, …).
    seedFullSlot(slot) {
      const v = {}
      this.attendees.fields.forEach(f => {
        if (f.type === 'static') return
        // Checkbox groups and native multi-selects hold an array of values.
        if (f.type === 'checkbox' || (f.type === 'select' && f.multiple)) {
          v[f.name] = Array.isArray(f.value) ? f.value.slice() : []
        } else {
          v[f.name] = f.value != null ? f.value : ''
        }
      })
      if (slot && slot.first_name) v.first_name = slot.first_name
      if (slot && slot.last_name) v.last_name = slot.last_name
      if (slot && slot.email) { v.email = slot.email; v.email_confirmation = slot.email }
      return v
    },
    // Identity-only defaults for guests (they complete their profile by email).
    seedMinimalSlot(slot) {
      return {
        first_name: (slot && slot.first_name) || '',
        last_name: (slot && slot.last_name) || '',
        email: (slot && slot.email) || '',
        email_confirmation: (slot && slot.email) || '',
      }
    },
    initAttendeeValues() {
      this.attendeeValues = this.attendeeSlots.map((slot, idx) =>
        idx === 0 && this.purchaserIsAttendee ? this.seedFullSlot(slot) : this.seedMinimalSlot(slot)
      )
      this.purchaserContact = this.blankPurchaserContact()
    },
    // Empty values object for every field the billing block will render.
    blankPurchaserContact() {
      const v = {}
      this.purchaserFields.forEach(f => { v[f.name] = f.type === 'checkbox' ? [] : '' })
      return v
    },
    // Carry over any overlapping values between card #0 and the billing block.
    transferValues(src, dst) {
      Object.keys(dst).forEach(k => {
        const val = src ? src[k] : undefined
        if (val == null) return
        if (Array.isArray(val)) { if (val.length) dst[k] = val.slice() }
        else if (val !== '') dst[k] = val
      })
      return dst
    },
    onPurchaserInput(field, value) {
      this.$set(this.purchaserContact, field.name, value)
    },
    setPurchaserIsAttendee(val) {
      if (val === this.purchaserIsAttendee) return

      if (!val) {
        // Buying for others: lift whatever billing/identity was in card #0 into
        // the billing block, then reset card #0 for a real (other) attendee.
        this.purchaserContact = this.transferValues(this.attendeeValues[0], this.blankPurchaserContact())
        this.$set(this.attendeeValues, 0, this.seedMinimalSlot(null))
      } else {
        // Back to attending: hand the billing values to card #0 and re-seed its
        // full form, then clear the billing block.
        const full = this.transferValues(this.purchaserContact, this.seedFullSlot(this.attendeeSlots[0]))
        this.$set(this.attendeeValues, 0, full)
        this.purchaserContact = this.blankPurchaserContact()
      }

      this.purchaserIsAttendee = val
    },
    onFieldInput({ index, name, value }) {
      if (!this.attendeeValues[index]) this.$set(this.attendeeValues, index, {})
      this.$set(this.attendeeValues[index], name, value)
    },
    async onFieldCoupon({ index, name, value }) {
      const slot = this.attendeeSlots[index]
      if (!slot) return
      const val = Array.isArray(value) ? (value[0] || '') : value
      try {
        await this.checkField({ id: slot.id, field_name: name, field_value: val })
        this.syncQtyFromServer()
      } catch (e) { /* ignore */ }
    },
    async onEmailBlur({ index, email }) {
      if (!this.settings.membership_discount_enabled || !email) return
      const slot = this.attendeeSlots[index]
      if (!slot) return
      try {
        await this.checkMembershipEmail({ id: slot.id, email })
        this.syncQtyFromServer()
      } catch (e) { /* ignore */ }
    },
    toggleAssign({ addonId, slotIdx }) {
      const list = this.addonAssignments[addonId] ? this.addonAssignments[addonId].slice() : []
      const i = list.indexOf(slotIdx)
      if (i >= 0) list.splice(i, 1)
      else {
        if (list.length >= this.addonTotalQty(addonId)) return
        list.push(slotIdx)
      }
      if (!list.length) this.$delete(this.addonAssignments, addonId)
      else this.$set(this.addonAssignments, addonId, list)
    },
    attendeeAddons(idx) {
      return this.cartedAddons.map(a => {
        let qty
        if (this.attendeeSlots.length === 1) qty = this.addonTotalQty(a.id)
        else qty = (this.addonAssignments[a.id] || []).includes(idx) ? 1 : 0
        return { id: a.id, selected_quantity: qty }
      })
    },
    buildAttendeePayload() {
      const buyingForOthers = this.attendeeSlots.length > 1 && !this.purchaserIsAttendee
      const attendees = this.attendeeSlots.map((slot, idx) => {
        // Only card #0 carries the full form, and only when the purchaser
        // attends. Everyone else sends identity-only.
        const values = idx === 0 && !buyingForOthers
          ? (this.attendeeValues[idx] || {})
          : this.seedMinimalSlot(this.attendeeValues[idx])
        return Object.assign({}, values, {
          id: slot.id,
          ticket_id: slot.ticket_id,
          addons: this.attendeeAddons(idx),
        })
      })

      const payload = { purchaser_is_attendee: !buyingForOthers, attendees }
      if (buyingForOthers) payload.purchaser = Object.assign({}, this.purchaserContact)
      return payload
    },
    async continueFromAttendee() {
      if (this.hasUnassigned) { this.scrollToBanner(); return }
      const ok = await this.$refs.attendeeForm.validate()
      if (!ok) { this.$nextTick(() => this.scrollToFirstError()); return }

      try {
        await this.updateAttendees(this.buildAttendeePayload())
      } catch (e) {
        if (e.errors) this.$refs.attendeeForm.setErrors(this.mapServerErrors(e.errors))
        this.$nextTick(() => this.scrollToFirstError())
        return
      }

      if (this.hasSessionsStep) {
        await this.fetchSessions()
        this.initSessionPicks()
        this.showStep('agenda')
      } else {
        await this.startPayment()
      }
    },
    dayShort(date) {
      // "Thursday, July 15, 2026" → "Thursday" for the day tab.
      return String(date || '').split(',')[0]
    },
    initSessionPicks() {
      // Restore previously-saved picks (agenda_selections) into the picker.
      const selected = (this.sessions.selectedSessionIds || []).map(Number)
      const picks = {}
      ;(this.sessions.days || []).forEach(d => d.sessions.forEach(g => {
        const hit = (g.items || []).find(it => selected.includes(Number(it.id)))
        if (hit) picks[g.group_id] = hit.id
      }))
      this.sessionPicks = picks
    },
    mapServerErrors(errors) {
      // Laravel keys like "attendees.0.email" → provider vids "email-0";
      // "purchaser.email" → "email-purchaser" (the billing block scope).
      const mapped = {}
      Object.keys(errors).forEach(key => {
        const a = key.match(/^attendees\.(\d+)\.(.+)$/)
        if (a) { mapped[`${a[2]}-${a[1]}`] = errors[key]; return }
        const p = key.match(/^purchaser\.(.+)$/)
        if (p) mapped[`${p[1]}-purchaser`] = errors[key]
      })
      return mapped
    },

    // ---------- Sessions step ----------
    isGroupBlocked(group) {
      // Only a real session pick (not "skip") in a concurrent group blocks this one.
      const realPickedGroupIds = Object.keys(this.sessionPicks)
        .filter(gid => this.sessionPicks[gid] && this.sessionPicks[gid] !== 'skip')
      return (group.concurrent_groups || []).some(cg => realPickedGroupIds.includes(cg))
    },
    onPick({ groupId, sessionId }) {
      this.$set(this.sessionPicks, groupId, sessionId)
      // Clear picks in groups that this real selection now blocks.
      if (sessionId !== 'skip') {
        this.$nextTick(() => {
          ;(this.sessions.days || []).forEach(d => d.sessions.forEach(g => {
            if (g.group_id !== groupId && this.isGroupBlocked(g) && this.sessionPicks[g.group_id]) {
              this.$delete(this.sessionPicks, g.group_id)
            }
          }))
        })
      }
    },
    async continueFromSessions() {
      // Send only real session ids — skipped blocks contribute nothing.
      const ids = Object.values(this.sessionPicks).filter(v => v && v !== 'skip')
      try {
        await this.updateSessions(ids)
      } catch (e) {
        this.$store.commit('registration/setError', e.message)
        return
      }
      await this.startPayment()
    },

    // ---------- Payment ----------
    async startPayment() {
      try {
        await this.preparePayment({})
      } catch (e) {
        this.$store.commit('registration/setError', e.message)
        return
      }
      if (this.isFree || !this.paymentInfo.payment_required) {
        const result = await this.confirmPayment()
        this.handlePaymentSuccess(result)
      } else {
        this.showStep('pay')
      }
    },
    submitPayment() {
      if (this.$refs.payment) this.$refs.payment.submit()
    },

    // ---------- Payment success (parity with the old registration form) ----------
    // Fires conversion tracking, then either redirects to the organizer's
    // custom success page (when enabled) or shows the in-page confirmation with
    // the configured payment message.
    handlePaymentSuccess(result) {
      this.trackConversion(result)

      if (this.settings.custom_success_enabled && this.settings.custom_success_url) {
        const total = Number(this.totalPrice)
        const url = `${this.settings.custom_success_url}?event=${encodeURIComponent(this.pathname)}&total=${total}`
        window.location.replace(url)
        return
      }

      this.doneMessage = (this.isFree ? this.settings.free_payment_message : this.settings.success_payment_message) || this.doneMessage
      this.showStep('done')
    },
    trackConversion(result) {
      const paymentId = result && result.payment_id
      if (!paymentId) return
      const total = Number(this.totalPrice)

      if (this.settings.google_analytics_id || this.settings.google_tag_manager_id) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'eec.purchase',
          ecommerce: {
            currencyCode: this.settings.currency,
            purchase: { actionField: { id: paymentId, revenue: total } },
          },
        })
      }

      if (this.settings.google_ads_id && this.settings.google_ads_conversion_label
        && typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion(Math.round(total * 100), paymentId)
      }
    },
    backFromPayment() {
      this.showStep(this.hasSessionsStep ? 'agenda' : 'attendee')
    },

    // ---------- Navigation / modals ----------
    showStep(name) {
      this.currentStep = name
      this.$store.commit('registration/setError', '')
      // Wait for the swap to render before scrolling. The steps are v-show, so
      // the document only shrinks once Vue applies it — scrolling first left
      // the browser clamping the request against the old, taller layout, which
      // is why mobile landed mid-page instead of at the new step's heading
      // (the tickets step is far taller than the ones after it).
      //
      // Target <main class="content"> rather than the document top: on mobile
      // the rail stacks above it, so page-top would park the user on the order
      // summary instead of the step they just moved to. This lands the stepper
      // ("Step 2 of 3") at the top edge, which is also where the new step's
      // heading is. On desktop the rail is a side column, so .content already
      // starts at y=0 and this behaves exactly like before.
      //
      // Instant rather than smooth: from the bottom of a long tickets step a
      // smooth scroll takes seconds on a phone and any touch aborts it midway,
      // so the user could still end up stranded. A jump is also what checkout
      // flows conventionally do between steps.
      this.$nextTick(() => {
        const anchor = this.$el.querySelector('.content')
        const top = anchor ? anchor.getBoundingClientRect().top + window.scrollY : 0
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' })
      })
    },
    scrollToBanner() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.unassigned-banner')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    scrollToFirstError() {
      const el = this.$el.querySelector('.field-error')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    async openAgenda() {
      // Load agenda first so we can tell single- from multi-session events.
      try { await this.fetchAgenda() } catch (e) { /* ignore */ }
      const sessions = (this.agenda && this.agenda.sessions) || []
      // Single-session event: skip the list modal and open the detail drawer directly.
      if (sessions.length === 1) {
        this.openSessionDrawer(sessions[0])
        return
      }
      this.showAgenda = true
    },
    closeAgenda() { this.showAgenda = false },
    openCe() { this.showCe = true },
    closeCe() { this.showCe = false },
    async openSessionDrawer(s) {
      this.drawerSession = s
      // Open immediately, then pull the agenda so drawerPresenters can upgrade
      // the sessions step's name-only presenters to full records. Failure just
      // leaves the plainer rows in place.
      try { await this.fetchAgenda() } catch (e) { /* ignore */ }
    },
    closeDrawer() { this.drawerSession = null },

    // Presenter rows open the same detail drawer as the landing page. The
    // session payload already carries name/role/photo/bio, so the drawer can
    // render at once; the roster adds the sessions this person leads.
    async openFacultyDetail(presenter) {
      this.drawerSession = null
      this.facultyDetail = presenter
      try { await this.fetchFaculty() } catch (e) { return }
      const match = this.findFaculty(presenter)
      // Guard against a second presenter having been opened while we waited.
      if (match && this.facultyDetail === presenter) this.facultyDetail = match
    },
    closeFacultyDetail() { this.facultyDetail = null },
    findFaculty(presenter) {
      if (!presenter) return null
      return this.facultyList.find((f) => (
        (presenter._id && f._id === presenter._id) || (presenter.id && f.id === presenter.id)
      )) || null
    },
    openSessionFromFaculty(session) {
      this.facultyDetail = null
      // Roster sessions are a thin projection (no description/objectives);
      // resolve to the agenda record so the drawer is fully populated.
      const detailed = this.agendaSessions.find((s) => s._id === session._id)
      this.openSessionDrawer(detailed || session)
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  min-height: 100vh;
}
#attendee-cards { max-width: 720px; }

/* Purchaser attendance choice + billing-only block (multi-ticket). */
.purchaser-choice { max-width: 720px; margin-bottom: 20px; }
.purchaser-choice .choice-label {
  font-size: var(--fs-body); font-weight: 500; color: var(--text); margin: 0 0 10px;
}
.choice-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 640px) { .choice-grid { grid-template-columns: 1fr; } }
.choice {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-md);
  cursor: pointer; background: var(--surface);
  transition: border-color 0.15s, background 0.15s;
}
.choice:hover { border-color: var(--brand-light); background: var(--surface-alt); }
.choice.selected { border-color: var(--brand); background: var(--brand-soft); }
.choice .radio-dot {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid var(--border-strong); flex-shrink: 0; margin-top: 1px;
  display: flex; align-items: center; justify-content: center;
}
.choice.selected .radio-dot { border-color: var(--brand); }
.choice.selected .radio-dot::after {
  content: ''; width: 9px; height: 9px; border-radius: 50%; background: var(--brand);
}
.choice .choice-t { font-size: var(--fs-body); font-weight: 500; color: var(--text); }
.choice .choice-d { font-size: var(--fs-label); color: var(--text-secondary); margin-top: 3px; }

.billing-card {
  max-width: 720px;
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 24px 26px;
  margin-bottom: 16px;
}
.billing-head { display: flex; align-items: center; gap: 10px; }
.billing-card .billing-ic { color: var(--brand); display: inline-flex; }
.billing-card .billing-t { font-size: var(--fs-lead); font-weight: 500; color: var(--brand-deep); }
.billing-card .billing-sub { font-size: var(--fs-label); color: var(--text-secondary); margin: 4px 0 16px; }

.topbar {
  background: rgba(255,255,255,0.92);
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 0.5px solid var(--border);
  padding: 16px 40px;
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.step-num-label {
  font-size: var(--fs-label);
  color: var(--text-tertiary);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}
.reg-error-banner {
  margin: 16px 40px 0;
  background: #fdecea;
  border: 0.5px solid #f5c2bd;
  color: #922;
  border-radius: var(--radius-md);
  padding: 12px 16px;
  font-size: var(--fs-small);
}
.tickets-empty { padding: 32px; text-align: center; color: var(--text-tertiary); font-size: var(--fs-body); }

.step-container {
  padding: 0 40px 140px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.step-header {
  margin: 0 -40px 28px;
  padding: 28px 40px;
  background: var(--bg);
  border-bottom: 0.5px solid var(--border);
  position: sticky;
  top: var(--topbar-h);
  z-index: 9;
}
.step-header h2.step-h {
  // one-off display size, kept literal so desktop is untouched (step heading)
  font-size: 28px;
  font-weight: 500;
  line-height: 1.1;
  margin: 0 0 6px;
  color: var(--brand-deep);
  letter-spacing: -0.01em;
}
.step-header .sub { font-size: var(--fs-body-lg); color: var(--text-secondary); }
.step-header .sub a { color: var(--brand-deep); font-weight: 500; text-decoration: none; }
.step-header .sub a:hover { text-decoration: underline; }
/* Sits above the category cards; matches the weight of `.sub` so the header
   doesn't grow a second competing headline. */
.category-select-heading {
  margin-top: 16px;
  font-size: var(--fs-body);
  font-weight: 500;
  color: var(--text);
}
/* Phone-only event banner (desktop has the rail); shown in the 640px block. */
.event-banner, .tickets-intro, .m-coupon, .sessions-progress, .cta-label-short { display: none; }
.tickets-area {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 24px;
  align-items: start;
}
@media (max-width: 1100px) { .tickets-area { grid-template-columns: 1fr; } }
.ticket-list {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
/* Wrapper so the offline-payment notice stacks above the ticket list while
   the details panel keeps its own grid column. */
.ticket-col { min-width: 0; }

/* Offline payments notice — theme-coloured (not a generic alert) because it
   is reassurance about this event's checkout, not a system warning. */
.pay-options {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-left: 3px solid var(--brand);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
.pay-options__ic {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--brand-soft);
  color: var(--brand-deep);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pay-options__body { min-width: 0; }
.pay-options__t {
  margin: 0 0 3px;
  font-size: var(--fs-body);
  font-weight: 600;
  color: var(--text);
}
.pay-options__d {
  margin: 0;
  font-size: var(--fs-small);
  color: var(--text-secondary);
}
@media (max-width: 640px) {
  .pay-options { padding: 14px 16px; gap: 12px; }
}
.btn-primary, .btn-outline {
  font-family: inherit;
  font-size: var(--fs-body);
  border-radius: var(--radius-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.15s, background 0.15s, transform 0.05s;
}
.btn-primary {
  background: var(--brand);
  color: white;
  border: none;
  padding: 14px 22px;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
  font-size: var(--fs-body-lg);
}
.btn-primary:hover { opacity: 0.92; box-shadow: var(--shadow-md); }
.btn-primary:active { transform: scale(0.98); }
.btn-primary:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
.btn-outline {
  background: transparent;
  color: var(--text);
  border: 0.5px solid var(--border-strong);
  padding: 10px 18px;
}
.btn-outline:hover { background: var(--surface); }
.unassigned-banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fef5e7;
  border: 0.5px solid #f0c674;
  border-radius: var(--radius-md);
  padding: 12px 16px;
  margin: 14px 0 0;
  font-size: var(--fs-small);
  color: #6b4d0d;
  line-height: 1.5;
}
.unassigned-banner svg { flex-shrink: 0; color: #b8860b; margin-top: 1px; }
.day-section { margin-bottom: 32px; }
.day-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1.5px solid var(--brand);
}
// one-off display size, kept literal so desktop is untouched
.day-header h2 { font-size: 28px; font-weight: 500; margin: 0; color: var(--brand-deep); }
.confirm { text-align: center; padding: 80px 20px; max-width: 560px; margin: 0 auto; }
.confirm-icon {
  width: 72px; height: 72px; border-radius: 50%;
  background: var(--brand-soft); color: var(--brand-deep);
  display: inline-flex; align-items: center; justify-content: center;
  margin-bottom: 20px;
}
// one-off display size, kept literal so desktop is untouched
.confirm h2 { font-size: 28px; font-weight: 500; margin: 0 0 8px; }
.confirm-sub { font-size: var(--fs-body-lg); color: var(--text-secondary); margin-bottom: 28px; }
.step-footer {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  height: var(--footer-h);
  background: var(--surface);
  border-top: 0.5px solid var(--border);
  padding: 14px 40px 14px calc(320px + 40px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  z-index: 40;
  box-shadow: 0 -4px 16px rgba(0,0,0,0.06);
}
.footer-progress { font-size: var(--fs-small); color: var(--text-secondary); display: flex; align-items: center; gap: 12px; }
.footer-progress strong { color: var(--brand-deep); font-weight: 500; }
.footer-actions { display: flex; gap: 8px; }
/* Hidden on desktop, where the rail's total is always in view. */
.footer-total { visibility: hidden; }
@media (max-width: 640px) {
  .footer-total {
    visibility: visible;
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }
  .footer-total__label { font-size: var(--fs-caption); color: var(--text-secondary); }
  .footer-total__amount { font-size: var(--fs-body-lg); font-weight: 500; color: var(--text); }
}
.day-jump { display: flex; gap: 4px; flex-wrap: wrap; }
.day-jump button {
  background: var(--surface-alt);
  border: 0.5px solid var(--border);
  padding: 7px 14px;
  font-size: var(--fs-small);
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  font-family: inherit;
  font-weight: 500;
}
.day-jump button.active { background: var(--brand); color: white; border-color: var(--brand); }
.picker-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 14px;
}
.picker-controls .search-input { flex: 1; min-width: 220px; }
.picker-controls .topic-filter { min-width: 160px; max-width: 200px; }
.topic-filter { font-size: var(--fs-small); min-width: 160px; max-width: 200px; }
.search-input { position: relative; }
.search-input input { padding-left: 36px; }
.search-input .icon {
  position: absolute; left: 12px; top: 50%;
  transform: translateY(-50%); color: var(--text-tertiary);
  pointer-events: none; display: flex; align-items: center;
}
/* Full agenda modal — ported 1:1 from the landing page's own agenda modal
   (resources/assets/sass/apps/event/multiple/landing_default/_agenda-modal.scss
   and _agenda.scss's .session-card rules) so both surfaces match exactly. */
.agenda-search {
  display: flex; align-items: center; gap: 8px; flex: 1; min-width: 240px;
  background: var(--surface); border: 0.5px solid var(--border);
  border-radius: var(--radius-md); padding: 0 14px;
}
.agenda-search:focus-within { border-color: var(--brand); box-shadow: 0 0 0 3px var(--brand-soft); }
.agenda-search svg { color: var(--text-tertiary); flex-shrink: 0; }
.agenda-search input {
  border: none !important; outline: none; background: transparent; flex: 1;
  font-family: inherit; font-size: var(--fs-small); padding: 11px 0; color: var(--text);
}
.agenda-search input:focus { box-shadow: none !important; }
.agenda-filters { display: flex; gap: 8px; }
.agenda-select {
  appearance: none; -webkit-appearance: none;
  background: var(--surface) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 12px center;
  border: 0.5px solid var(--border); border-radius: var(--radius-md);
  padding: 10px 32px 10px 14px; font-family: inherit; font-size: var(--fs-small);
  color: var(--text); cursor: pointer;
}
.agenda-empty { text-align: center; padding: 60px 20px; color: var(--text-tertiary); font-size: var(--fs-body); }
.agenda-day-section { margin-bottom: 36px; }
.agenda-day-section:last-child { margin-bottom: 0; }
.agenda-day-section-title {
  font-size: var(--fs-body); font-weight: 500; color: var(--brand-deep);
  text-transform: uppercase; letter-spacing: 0.08em;
  margin: 0 0 14px; padding-bottom: 10px; border-bottom: 0.5px solid var(--border);
}
.session-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 800px) { .session-cards { grid-template-columns: 1fr; } }
.session-card {
  background: var(--surface); border: 0.5px solid var(--border);
  border-radius: var(--radius-lg); padding: 22px 24px;
  transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
}
.session-card:hover { border-color: var(--brand-light); transform: translateY(-1px); box-shadow: var(--shadow-sm); }
.session-card-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.session-time { font-size: var(--fs-label); font-variant-numeric: tabular-nums; color: var(--brand-deep); font-weight: 500; letter-spacing: 0.01em; }
.session-track-pill { font-size: var(--fs-caption); font-weight: 500; padding: 3px 9px; border-radius: 999px; letter-spacing: 0.04em; text-transform: uppercase; }
.session-track-pill[data-track="1"] { background: #e8eee5; color: #344a2f; }
.session-track-pill[data-track="2"] { background: #e0eaf2; color: #1e3a5f; }
.session-track-pill[data-track="3"] { background: #e1f5ee; color: #08503d; }
.session-track-pill[data-track="4"] { background: #faece7; color: #712b13; }
.session-track-pill[data-track="5"] { background: #eeedfe; color: #3c3489; }
.session-card h3 { font-size: var(--fs-lead); font-weight: 500; margin: 0 0 6px; color: var(--text); line-height: 1.35; }
.session-presenter { font-size: var(--fs-small); color: var(--text-secondary); margin: 0 0 12px; }
.session-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.session-tag { font-size: var(--fs-label); font-weight: 500; background: var(--surface-alt); color: var(--text-secondary); padding: 3px 9px; border-radius: var(--radius-sm); }
/* Event topics — sage pills, mirroring the landing hero/CE card chips.
   The sage and cream-card vars are theme aliases, so they adapt per registration theme. */
.topic-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.ce-topics-row { display: flex; flex-wrap: wrap; gap: 8px; }
/* Separate the chips from the big CE-hours stat only when the stat is present. */
.ce-topics-row--with-stat { margin-top: 16px; padding-top: 16px; border-top: 0.5px solid var(--border); }
.topic-chip {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 14px; border-radius: 999px;
  background: var(--sage-soft); color: var(--text);
  font-size: var(--fs-small); font-weight: 600; line-height: 1; white-space: nowrap;
}
.topic-chip--h { padding-right: 6px; }
.topic-chip-h {
  padding: 3px 8px; border-radius: 999px;
  background: var(--cream-card); color: var(--sage-deep);
  font-size: var(--fs-caption); font-weight: 800;
}
/* The session drawer's body is <lp-session-detail> (ui/lp-session-detail.vue),
   which carries its own styles — shared with the landing page's drawer. */
.ce-section { margin-bottom: 26px; }
.ce-section:last-child { margin-bottom: 4px; }
.ce-section h4 {
  display: flex; align-items: center; gap: 8px;
  font-size: var(--fs-label); text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--text-tertiary); margin: 0 0 12px; font-weight: 600;
}
.ce-h4-ic { display: inline-flex; color: var(--brand); flex-shrink: 0; }
.ce-h4-ic svg { width: 15px; height: 15px; display: block; }
/* Hang the header icon in a left gutter so the title text and the body copy
   below share the same left edge (icon width 15px + 8px gap = 23px). */
.ce-section--doc { padding-left: 23px; }
.ce-section--doc h4 { margin-left: -23px; }
.ce-section p { font-size: var(--fs-body); line-height: 1.65; margin: 0 0 8px; color: var(--text-secondary); }
.ce-list { margin: 0; padding-left: 20px; font-size: var(--fs-body); line-height: 1.7; color: var(--text-secondary); }
.ce-list li { margin-bottom: 6px; }

/* Callouts (Important / hours-differ Note) — accent bar + icon. */
.ce-callout {
  display: flex; gap: 12px; align-items: flex-start;
  background: var(--brand-soft);
  border: 0.5px solid var(--border);
  border-left: 3px solid var(--brand);
  border-radius: var(--radius-md); padding: 13px 16px;
}
.ce-callout p { margin: 0; font-size: var(--fs-small); line-height: 1.55; color: var(--text-secondary); }
.ce-callout strong { color: var(--brand-deep); font-weight: 600; }
.ce-callout-ic { flex-shrink: 0; color: var(--brand); margin-top: 1px; }
.ce-callout-ic svg { width: 17px; height: 17px; display: block; }
.ce-callout--muted { background: var(--surface-alt); border-left-color: var(--border-strong); }
.ce-callout--muted .ce-callout-ic { color: var(--text-tertiary); }
.ce-callout--muted strong { color: var(--text); }

/* CE format columns — one card per attendance type. */
.ce-formats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ce-formats-grid--single { grid-template-columns: 1fr; }
@media (max-width: 720px) { .ce-formats-grid { grid-template-columns: 1fr; } }
.ce-format-column {
  background: var(--surface); border: 0.5px solid var(--border);
  border-radius: var(--radius-lg); padding: 20px 22px;
}
.ce-format-header { margin-bottom: 16px; }
.ce-format-badge {
  display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px 5px 9px;
  border-radius: 999px; font-size: var(--fs-caption); font-weight: 600;
  letter-spacing: 0.05em; text-transform: uppercase;
}
.ce-format-badge-ic { display: inline-flex; }
.ce-format-badge-ic svg { width: 13px; height: 13px; display: block; }
.ce-format-badge-inperson { background: var(--brand-soft); color: var(--text); }
.ce-format-badge-virtual { background: #e3eaf3; color: #142844; }

/* Hero stat — the credential is the star. Shown once, above the format columns. */
.ce-stat-card {
  background: var(--surface); border: 0.5px solid var(--border);
  border-radius: var(--radius-lg); padding: 20px 22px; margin-bottom: 16px;
}
.ce-stat { display: flex; align-items: baseline; gap: 12px; }
.ce-stat-num {
  // decorative numeral, not tokenized
  font-size: 44px; font-weight: 600; line-height: 0.9;
  color: var(--brand-deep); letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.ce-stat-unit {
  font-size: var(--fs-label); font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--text-tertiary);
  white-space: nowrap; line-height: 1.3;
}

/* Accrediting bodies. */
.ce-approvals { margin-top: 18px; padding-top: 16px; border-top: 0.5px solid var(--border); }
.ce-approvals-head {
  display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  font-size: var(--fs-caption); text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-tertiary); font-weight: 600;
}
.ce-approvals-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px;
  background: var(--brand-soft); color: var(--text);
  font-size: var(--fs-caption); font-weight: 600; letter-spacing: 0;
}
.ce-body-card {
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: var(--radius-md); padding: 12px 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ce-body-card + .ce-body-card { margin-top: 8px; }
.ce-body-card:hover { border-color: var(--brand-light); box-shadow: var(--shadow-sm); }
/* Logo cards use a media layout: logo column left (vertically centered), content right. */
.ce-body-card--logo { display: flex; align-items: center; gap: 20px; }
.ce-body-logo {
  flex: 0 0 auto; width: 104px;
  display: flex; align-items: center; justify-content: center;
}
.ce-body-logo img { max-width: 104px; max-height: 84px; width: auto; height: auto; object-fit: contain; display: block; }
.ce-body-main { flex: 1 1 auto; min-width: 0; }
.ce-body-name {
  display: flex; align-items: center; gap: 8px;
  font-size: var(--fs-small); font-weight: 600; color: var(--text); margin-bottom: 5px;
}
.ce-body-ic { flex-shrink: 0; color: var(--brand); display: inline-flex; }
.ce-body-ic svg { width: 15px; height: 15px; display: block; }
.ce-body-detail { font-size: var(--fs-label); color: var(--text-secondary); line-height: 1.55; }
.ce-body-detail p { margin: 0 0 4px; }
.ce-body-detail p:last-child { margin-bottom: 0; }

/* Subtle staggered reveal on open. */
@keyframes ceReveal { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: no-preference) {
  .agenda-modal-body > .ce-section { animation: ceReveal 0.4s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
  .agenda-modal-body > .ce-section:nth-child(2) { animation-delay: 0.05s; }
  .agenda-modal-body > .ce-section:nth-child(3) { animation-delay: 0.10s; }
  .agenda-modal-body > .ce-section:nth-child(4) { animation-delay: 0.15s; }
  .agenda-modal-body > .ce-section:nth-child(5) { animation-delay: 0.20s; }
  .agenda-modal-body > .ce-section:nth-child(6) { animation-delay: 0.25s; }
}

@media (max-width: 800px) {
  .page { grid-template-columns: 1fr; }
  /* Pills and "Step X of Y" share one row (label on the right) to keep the
     sticky topbar short. */
  .topbar {
    padding: 12px 20px;
    gap: 12px;
  }
  .topbar .stepper { min-width: 0; }
  .step-container { padding: 20px 20px 100px; }
  /* The header full-bleeds with negative margins matched to the container
     padding. On mobile the container padding shrinks to 20px, so the header
     bleed must shrink too — otherwise its -40px pull overhangs the viewport
     by 20px each side and forces a horizontal page scroll. */
  .step-header { margin: 0 -20px 24px; padding: 24px 20px; }
  .step-footer { padding: 12px 20px; }
}

/* Phones: the tickets step header keeps only the switchers (heading, intro
   and "registration type" label hidden), so the sticky block stays short. */
@media (max-width: 640px) {
  /* The topbar and the tickets header read as one white block. min-height
     matches the --topbar-h the sticky header parks at, so no content shows
     through a gap between them. */
  .topbar { border-bottom: 0; min-height: var(--topbar-h); }
  .step-num-label { font-size: 10px; }
  .step-header--attendee,
  .step-header--sessions { padding-top: 0; }

  /* Sessions step: progress sits above the footer instead of between buttons. */
  .step-footer .footer-progress { display: none; }
  /* Sits right under the last block card (its own 14px bottom margin is the gap). */
  #picker-content .day-section:last-child { margin-bottom: 0; }
  .sessions-progress {
    display: block;
    font-size: var(--fs-small);
    color: var(--text-secondary);
  }
  .sessions-progress strong { color: var(--brand-deep); font-weight: 500; }
  .cta-label-full { display: none; }
  .cta-label-short { display: inline; }
  .step-header--tickets {
    background: var(--surface);
    /* -20px top cancels the step-container's phone top padding, so the
       header sits flush under the topbar before it starts sticking too. */
    margin-top: -20px;
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .step-header--tickets .step-h,
  .step-header--tickets .sub,
  .step-header--tickets .category-select-heading { display: none; }
  /* Spacing comes from the flex gap instead. */
  .step-header--tickets .attend-toggle-inline,
  .step-header--tickets ::v-deep .category-select { margin-top: 0; }
  /* Nothing left to show (single attendance type, no categories). */
  .step-header--bare { display: none; }

  .event-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: -8px 0 16px;
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--brand-deep);
    color: #fff;
  }
  .event-banner__icon {
    width: 40px;
    height: 40px;
    flex: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.14);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .event-banner__body { min-width: 0; }
  .event-banner__name { font-size: var(--fs-small); font-weight: 600; line-height: 1.3; }
  .event-banner__meta { font-size: var(--fs-caption); color: rgba(255, 255, 255, 0.8); margin-top: 2px; }
  .event-banner__loc { display: flex; align-items: flex-start; gap: 5px; }
  .event-banner__loc svg { flex: none; margin-top: 2px; }

  .tickets-intro { display: block; margin-bottom: 16px; }
  .tickets-intro__h {
    // one-off display size, matches the desktop step heading's scale on phones
    font-size: 22px;
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.01em;
    margin: 0 0 4px;
    color: var(--brand-deep);
  }
  .tickets-intro__sub { font-size: var(--fs-small); line-height: 1.45; color: var(--text-secondary); }
  .tickets-intro__links { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 4px; font-size: var(--fs-small); }
  .tickets-intro__links a { color: var(--brand); }
  .tickets-intro__links a:hover { color: var(--brand-deep); }

  /* Separate cards instead of one bordered list (card styles live in
     lp-ticket-card). */
  .ticket-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: none;
    border: 0;
    border-radius: 0;
    overflow: visible;
  }

  .m-coupon { display: flex; flex-direction: column; gap: 6px; margin-top: 16px; }
  .m-coupon__label {
    font-size: var(--fs-caption);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-secondary);
  }
  .m-coupon__row { display: flex; gap: 8px; }
  .m-coupon__row input {
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 12px;
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    background: var(--surface);
    // 16px keeps iOS Safari from zooming into the field on focus
    font-size: 16px;
    font-family: inherit;
    color: var(--text);
  }
  .m-coupon__apply {
    height: 44px;
    padding: 0 16px;
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    background: var(--surface);
    font-family: inherit;
    font-size: var(--fs-small);
    font-weight: 600;
    color: var(--brand-deep);
    cursor: pointer;
  }
  .m-coupon__row input:focus-visible,
  .m-coupon__apply:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
  .m-coupon__error { font-size: var(--fs-caption); color: var(--danger); }
}

/* Mobile: the day tabs become a single horizontal-scroll strip instead of
   wrapping into a tall block. With many event days (e.g. a certification
   series) the wrapped tabs otherwise ate the whole agenda bottom-sheet,
   squeezing the session list to a few px so it couldn't be scrolled. */
@media (max-width: 640px) {
  .day-jump {
    flex: 1 1 100%;
    min-width: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .day-jump::-webkit-scrollbar { display: none; }
  .day-jump button { flex-shrink: 0; white-space: nowrap; }
}
</style>
